import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AuthLayout } from "@/components/layout/AuthLayout";
import { Button } from "@/components/ui/button";
import { FormMessage } from "@/components/ui/form-message";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AuthActionError,
  authErrorToTranslationKey,
  signUpWithPassword,
} from "@/features/auth/api/authClient";
import {
  type RegisterFormValues,
  type RegisterSubmissionValues,
  registerSchema,
} from "@/features/auth/validation/authSchemas";

export function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<RegisterFormValues, undefined, RegisterSubmissionValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      password: "",
      phone: "",
    },
  });

  async function onSubmit(values: RegisterSubmissionValues) {
    setSubmitError(null);

    try {
      await signUpWithPassword(values);
      navigate("/verify-email-pending", {
        replace: true,
        state: { email: values.email },
      });
    } catch (error) {
      if (error instanceof AuthActionError) {
        setSubmitError(t(authErrorToTranslationKey(error.code)));
        return;
      }

      setSubmitError(t("auth.errors.generic"));
    }
  }

  return (
    <AuthLayout
      eyebrow={t("auth.meta.eyebrow")}
      title={t("auth.register.title")}
      subtitle={t("auth.register.subtitle")}
      footer={
        <p className="text-sm text-ink/60">
          {t("auth.actions.haveAccount")}{" "}
          <Link className="font-semibold text-ink" to="/login">
            {t("auth.login.submit")}
          </Link>
        </p>
      }
    >
      <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="first_name">{t("auth.fields.firstName")}</Label>
            <Input id="first_name" {...form.register("first_name")} />
            {form.formState.errors.first_name ? (
              <FormMessage>{t(form.formState.errors.first_name.message ?? "auth.errors.generic")}</FormMessage>
            ) : null}
          </div>
          <div>
            <Label htmlFor="last_name">{t("auth.fields.lastName")}</Label>
            <Input id="last_name" {...form.register("last_name")} />
            {form.formState.errors.last_name ? (
              <FormMessage>{t(form.formState.errors.last_name.message ?? "auth.errors.generic")}</FormMessage>
            ) : null}
          </div>
        </div>

        <div>
          <Label htmlFor="email">{t("auth.fields.email")}</Label>
          <Input id="email" type="email" autoComplete="email" {...form.register("email")} />
          {form.formState.errors.email ? (
            <FormMessage>{t(form.formState.errors.email.message ?? "auth.errors.generic")}</FormMessage>
          ) : null}
        </div>

        <div>
          <Label htmlFor="password">{t("auth.fields.password")}</Label>
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            {...form.register("password")}
          />
          {form.formState.errors.password ? (
            <FormMessage>{t(form.formState.errors.password.message ?? "auth.errors.generic")}</FormMessage>
          ) : null}
        </div>

        <div>
          <Label htmlFor="phone">{t("auth.fields.phone")}</Label>
          <Input id="phone" placeholder="+57 300 555 0101" {...form.register("phone")} />
          {form.formState.errors.phone ? (
            <FormMessage>{t(form.formState.errors.phone.message ?? "auth.errors.generic")}</FormMessage>
          ) : null}
        </div>

        {submitError ? <FormMessage>{submitError}</FormMessage> : null}

        <Button className="w-full" type="submit" disabled={form.formState.isSubmitting}>
          {form.formState.isSubmitting ? t("common.saving") : t("auth.register.submit")}
        </Button>
      </form>
    </AuthLayout>
  );
}
